import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyableReference } from "@/components/ui/copyable-reference";
import { skipMfaEnrolment } from "@/lib/mfa-api";
import { useAuthStore } from "@/stores/auth-store";

/**
 * The ENROLMENT_SUGGESTED prompt (ALL-5747, specs/alloy-mfa S-2, S-13): at
 * organization level `enrol` the session may use the product, and the shell
 * offers enrolment above every page until the person sets up a factor or
 * chooses "Not now". "Not now" calls POST /v1/mfa/enrolments/skip, which the
 * auth service audits (MFA_ENROLMENT_SKIPPED); the prompt then stays hidden
 * for the rest of this sign-in.
 */
export const MFA_PROMPT_COPY = {
  label: "Two-step sign-in",
  text: "Protect your Alloy account with a passkey or an authenticator app.",
  setUp: "Set up now",
  notNow: "Not now",
  skipFailed: "We couldn't record that. Try again.",
} as const;

export function MfaEnrolmentPrompt() {
  const startMfaEnrolment = useAuthStore((s) => s.startMfaEnrolment);
  const mfaEnrolmentSkipped = useAuthStore((s) => s.mfaEnrolmentSkipped);
  const initSession = useAuthStore((s) => s.initSession);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<{ supportReference?: string } | null>(null);

  async function notNow() {
    setBusy(true);
    setFailed(null);
    const result = await skipMfaEnrolment();
    setBusy(false);
    if (result.ok) {
      mfaEnrolmentSkipped();
      return;
    }
    if (result.kind === "required" || result.kind === "unauthenticated") {
      // The session's 2FA state changed: let GET /auth/session decide what shows.
      void initSession();
      return;
    }
    setFailed({ supportReference: result.supportReference });
  }

  return (
    <div
      role="region"
      aria-label={MFA_PROMPT_COPY.label}
      data-testid="mfa-enrolment-prompt"
      className="flex flex-wrap items-center gap-3 border-b border-info-border bg-info-soft px-4 py-2.5 text-xs text-info"
    >
      <ShieldCheck className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1 leading-relaxed">{MFA_PROMPT_COPY.text}</p>
      {failed && (
        <span role="alert" className="flex items-center gap-2 text-danger">
          {MFA_PROMPT_COPY.skipFailed}
          <CopyableReference reference={failed.supportReference} compact />
        </span>
      )}
      <Button size="sm" onClick={startMfaEnrolment} disabled={busy}>
        {MFA_PROMPT_COPY.setUp}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => void notNow()} disabled={busy}>
        {MFA_PROMPT_COPY.notNow}
      </Button>
    </div>
  );
}
